"use client";

import { Suspense, useEffect, useState } from "react";
import { useSession, signIn } from "@/lib/auth/client";
import { useCredits } from "@/hooks/use-credits";
import { useCheckout } from "@/hooks/use-checkout";
import { Coins, Shield, Clock, Download, Sparkles } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import { toast } from "sonner";
import { sendGAEvent } from "@/lib/analytics";
import { getEffectiveCredits, isPromotionEnabled } from "@/lib/payments/client";
import { CreditPricingGrid } from "@/components/credits/credit-pricing-grid";
import { getDisplayCreditPlansClient, type CreditPlan, type CreditPackId } from "@/lib/payments/plans";
import {
  canUseFreePreviewOnCreditsPage,
  freePreviewEligibilityFromCredits,
} from "@/lib/payments/free-preview-config";
import { trackCreditsPageViewed } from "@/lib/posthog-client";

function AnimatedNumber({ value, duration = 1000 }: { value: number; duration?: number }) {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    let startTime: number;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setDisplayValue(Math.floor(progress * value));
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [value, duration]);

  return <span>{displayValue}</span>;
}

function CreditsPage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { data: session, isPending: sessionLoading } = useSession();
  const {
    data: credits,
    isLoading: creditsLoading,
    refetch: refetchCredits,
  } = useCredits();
  const checkout = useCheckout();
  const [processingPackId, setProcessingPackId] = useState<string | null>(null);

  const isAuthenticated = !!session?.user;
  const hasPurchasedAnyPack = credits?.hasPurchasedAnyPack ?? false;
  const hasCredits = (credits?.balance || 0) > 0;
  const canUseFreePreview = canUseFreePreviewOnCreditsPage(
    isAuthenticated,
    freePreviewEligibilityFromCredits(credits)
  );
  const isStarterPromoEnabled = isPromotionEnabled("starter");
  const starterPromoCredits = getEffectiveCredits("starter");
  const CREDIT_PLANS: CreditPlan[] = getDisplayCreditPlansClient({
    includeFree: false,
    isStarterPromoEnabled,
    starterPromoCredits,
  });

  // Handle success redirect from Polar
  useEffect(() => {
    if (searchParams.get("success") === "true") {
      toast.success("Payment successful! Your credits will be added shortly.");
      const timeout = setTimeout(() => {
        refetchCredits();
      }, 2000);
      router.replace("/credits");
      return () => clearTimeout(timeout);
    }
  }, [searchParams, refetchCredits, router]);

  // Track page view and conversion funnel
  useEffect(() => {
    sendGAEvent({
      action: "credits_viewed",
      category: "engagement",
      label: "credits_page",
    });
    // Track pricing page view for conversion funnel
    import("@/lib/analytics").then(({ trackViewPricing }) => {
      trackViewPricing();
    });
    // Track credits page view in PostHog
    trackCreditsPageViewed({
      isAuthenticated,
      creditBalance: credits?.balance,
      hasPurchasedAnyPack,
    });
  }, [isAuthenticated, credits?.balance, hasPurchasedAnyPack]);

  if (sessionLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-3.5rem)] bg-[#0B0D10]">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-16 h-16 rounded-full border-2 border-primary/20 border-t-primary animate-spin" />
            <Coins className="w-6 h-6 text-primary absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          </div>
          <p className="text-[#9AA3B2] text-sm animate-pulse">Loading...</p>
        </div>
      </div>
    );
  }

  const handlePurchase = async (packId: CreditPackId) => {
    if (!isAuthenticated) {
      signIn.social({ provider: "google", callbackURL: "/credits" });
      return;
    }
    setProcessingPackId(packId);

    // If user is company member, include companyId in checkout
    // Only include companyId if it exists and user is a company member
    const checkoutData: { packId: CreditPackId; companyId?: string; source: "credits_page" } = {
      packId,
      source: "credits_page",
    };
    if (credits?.isCompanyMember && credits?.companyId) {
      checkoutData.companyId = credits.companyId;
    }
    checkout.mutate(checkoutData, {
      onError: (error) => {
        console.error("Checkout error:", error);
        setProcessingPackId(null);
      },
      onSettled: () => {
        // Note: onSettled won't run if redirect happens, but that's fine
        // The redirect happens in onSuccess, so we clear state there
      },
    });
  };

  const handlePurchasePaidPack = (planId: CreditPackId) => {
    void handlePurchase(planId);
  };

  return (
    <div className="min-h-[calc(100vh-3.5rem)] bg-[#0B0D10]">
      {/* Hero Section with Balance */}
      <section className="relative overflow-hidden">
        {/* Animated background gradient */}
        <div className="absolute inset-0 bg-linear-to-br from-primary/5 via-transparent to-violet-500/5" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/10 rounded-full blur-[120px] opacity-50" />
        
        <div className="relative max-w-6xl mx-auto px-4 pt-10 pb-10">
          {isAuthenticated ? (
            <div className="max-w-lg mx-auto">
              {/* Balance Card */}
              <div className="relative rounded-2xl bg-linear-to-br from-[#12151B] to-[#0F1218] border border-white/10 p-6 backdrop-blur-sm overflow-hidden">
                {/* Subtle shimmer accent */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                
                <div className="relative">
                  <p className="text-xs uppercase tracking-widest text-[#6B7280] mb-3">Previews Remaining</p>
                  
                  {creditsLoading ? (
                    <div className="h-14 w-40 bg-white/5 animate-pulse rounded-xl" />
                  ) : (
                    <div className="flex items-baseline gap-2.5 mb-4">
                      <span className="text-5xl md:text-6xl font-bold text-[#E6E8EC] tracking-tight">
                        <AnimatedNumber value={credits?.balance || 0} />
                      </span>
                      <span className="text-lg text-[#9AA3B2] font-medium">HD previews</span>
                    </div>
                  )}
                  
                  {/* Quick stats */}
                  <div className="flex items-center gap-4 text-xs text-[#6B7280]">
                    <div className="flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-primary/70" />
                      <span>Photorealistic quality</span>
                    </div>
                    <span className="w-px h-3 bg-white/10" />
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-primary/70" />
                      <span>Never expires</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center mb-4">
              <h1 className="text-5xl md:text-6xl font-bold text-[#E6E8EC] tracking-tight mb-4">
                Simple, Transparent Pricing
              </h1>
              <p className="text-xl text-[#9AA3B2] max-w-xl mx-auto">
                Pay only for what you use. No subscriptions. No hidden fees.
              </p>
            </div>
          )}
        </div>
      </section>

          {/* Pricing Section */}
      <section className="relative py-20 bg-[#0E1117]">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#1A1F28_1px,transparent_1px),linear-gradient(to_bottom,#1A1F28_1px,transparent_1px)] bg-size-[64px_64px] opacity-20" />
        
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#E6E8EC] mb-4">
              Choose Your Pack
            </h2>
            <p className="text-[#9AA3B2] text-lg max-w-xl mx-auto">
              One-time purchase. No subscriptions. Credits never expire.
            </p>
          </div>

          <CreditPricingGrid
            plans={CREDIT_PLANS}
            isAuthenticated={isAuthenticated}
            hasCredits={hasCredits}
            canUseFreePreview={canUseFreePreview}
            processingPackId={processingPackId as CreditPackId | null}
            onPurchasePaidPack={handlePurchasePaidPack}
            onFreePreviewClick={() =>
              signIn.social({ provider: "google", callbackURL: "/app" })
            }
          />
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 bg-[#0B0D10]">
        <div className="max-w-4xl mx-auto px-4">
          <div className="grid sm:grid-cols-3 gap-6">
              {[
                { icon: Shield, title: "Secure Payments", desc: "Protected by Polar.sh" },
                { icon: Clock, title: "Never Expires", desc: "Use credits anytime" },
                { icon: Download, title: "Full Downloads", desc: "No watermarks ever" },
              ].map((item, idx) => (
              <div 
                key={idx}
                className="flex items-center gap-4 p-5 rounded-2xl bg-[#12151B] border border-[#222836]"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <item.icon className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <div className="font-semibold text-[#E6E8EC]">{item.title}</div>
                  <div className="text-sm text-[#6B7280]">{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-[#0A0C0F]">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-[#E6E8EC] mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {[
              {
                q: "Why credits instead of a subscription?",
                a: "AI image generation has real compute costs. Credits let you pay only for what you use, without recurring charges eating into your wallet.",
              },
              {
                q: "Do credits expire?",
                a: "Never. Your credits stay in your account until you use them. No pressure, no deadlines.",
              },
              {
                q: "Can I download all generated images?",
                a: "Yes! Every image you generate is yours to download in full resolution. No watermarks, no restrictions.",
              },
              {
                q: "What quality are the previews?",
                a: "All previews are generated in HD quality, optimized for photorealism. The images are perfect for visualizing how wheels will look on your car.",
              },
            ].map((faq, idx) => (
              <div 
                key={idx}
                className="p-6 rounded-2xl bg-[#12151B] border border-[#222836] hover:border-[#2A2F3A] transition-colors"
              >
                <h3 className="font-semibold text-[#E6E8EC] mb-2">{faq.q}</h3>
                <p className="text-[#9AA3B2] text-sm leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default function CreditsPageWrapper() {
  return (
    <Suspense fallback={
      <div className="flex items-center justify-center min-h-[calc(100vh-3.5rem)] bg-[#0B0D10]">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-16 h-16 rounded-full border-2 border-primary/20 border-t-primary animate-spin" />
            <Coins className="w-6 h-6 text-primary absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          </div>
          <p className="text-[#9AA3B2] text-sm animate-pulse">Loading...</p>
        </div>
      </div>
    }>
      <CreditsPage />
    </Suspense>
  );
}
